import { effectiveSfxVolume } from './audio';

const CLICK_SRC = '/audio/click.mp3';
const HOVER_SRC = '/audio/hover.mp3';
let clickPool: HTMLAudioElement[] = [];
let hoverPool: HTMLAudioElement[] = [];
let clickIdx = 0;
let hoverIdx = 0;

function ensureClick(): HTMLAudioElement[] {
  if (clickPool.length > 0) return clickPool;
  clickPool = Array.from({ length: 4 }, () => {
    const a = new Audio(CLICK_SRC);
    a.preload = 'auto';
    a.volume = effectiveSfxVolume();
    return a;
  });
  return clickPool;
}

function ensureHover(): HTMLAudioElement[] {
  if (hoverPool.length > 0) return hoverPool;
  hoverPool = Array.from({ length: 2 }, () => {
    const a = new Audio(HOVER_SRC);
    a.preload = 'auto';
    a.volume = effectiveSfxVolume() * 0.5;
    return a;
  });
  return hoverPool;
}

/**
 * A UI sound must never be able to break its caller. `play()` can reject
 * (autoplay policy), return `undefined` instead of a Promise, or throw
 * synchronously — jsdom's unimplemented media backend does exactly that, and
 * a `.catch()` alone does not cover it. All three are swallowed here.
 */
function emit(el: HTMLAudioElement, volume: number): void {
  try {
    el.volume = volume;
    el.currentTime = 0;
    el.play()?.catch(() => {});
  } catch {
    // No audio backend, or the element is in a state that refuses playback.
  }
}

export function playClick(): void {
  const p = ensureClick();
  const el = p[clickIdx % p.length];
  clickIdx++;
  emit(el, effectiveSfxVolume());
}

/** Half the click's volume, so a hover never competes with a real action. */
function playHover(): void {
  const p = ensureHover();
  const el = p[hoverIdx % p.length];
  hoverIdx++;
  emit(el, effectiveSfxVolume() * 0.5);
}

const INTERACTIVE = 'button, a[href], [role="button"], [data-click-sound], input[type="checkbox"], .shell-tab-btn';

export function mountClickSound(root: HTMLElement): () => void {
  /**
   * The interactive element the pointer is currently inside, or null. The
   * hover sound plays only when this CHANGES, so crossing a button's nested
   * children (an icon <span>, a label) stays silent — `pointerover` fires per
   * element entered, but every one of them resolves to the same ancestor here.
   * Keyed on the resolved ancestor rather than `e.target` for exactly that
   * reason.
   */
  let hovered: Element | null = null;

  function clickHandler(e: Event): void {
    const target = e.target as HTMLElement;
    if (target.closest(INTERACTIVE)) playClick();
  }

  // `pointerover` (bubbles) rather than `pointerenter` + capture: one
  // delegated listener sees every transition, including button -> button
  // directly, which a per-element enter/leave pair would report as two
  // unordered events.
  function overHandler(e: Event): void {
    const el = (e.target as HTMLElement).closest(INTERACTIVE);
    if (el === hovered) return;
    hovered = el;
    if (el) playHover();
  }

  // Leaving the root entirely emits no further `pointerover`, so without this
  // the last-hovered element would stay latched and re-entering it would be
  // silent.
  function leaveHandler(): void {
    hovered = null;
  }

  root.addEventListener('pointerdown', clickHandler, { passive: true });
  root.addEventListener('pointerover', overHandler, { passive: true });
  root.addEventListener('pointerleave', leaveHandler, { passive: true });
  return () => {
    root.removeEventListener('pointerdown', clickHandler);
    root.removeEventListener('pointerover', overHandler);
    root.removeEventListener('pointerleave', leaveHandler);
    hovered = null;
  };
}
